import React, { useEffect, useState } from "react";
import { PageHeader } from "../../components/Shell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Power } from "lucide-react";

export default function Organizations() {
  const [orgs, setOrgs] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/organizations");
      setOrgs(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/organizations", form);
      toast.success("Organization created");
      setForm({ name: "", description: "" });
      setShowAdd(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (id) => {
    try {
      await api.patch(`/organizations/${id}/toggle`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="orgs-page">
      <PageHeader
        eyebrow="Super Admin"
        title="Organizations"
        actions={
          <button
            onClick={() => setShowAdd((v) => !v)}
            className="px-4 py-2 rounded-md bg-[#05126E] text-white font-semibold hover:bg-[#030B45] transition-colors flex items-center gap-2"
            data-testid="open-create-org-btn"
          >
            <Plus className="w-4 h-4" /> New org
          </button>
        }
      />

      {showAdd && (
        <form
          onSubmit={create}
          className="mb-8 border border-[#D2D8D4] rounded-md bg-white p-5 grid md:grid-cols-3 gap-3 items-end"
          data-testid="create-org-form"
        >
          <div>
            <label className="text-xs font-bold uppercase tracking-widest">Name</label>
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 w-full px-3 py-2 border border-[#D2D8D4] rounded-md bg-white"
              data-testid="org-name-input"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest">Description</label>
            <input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-1 w-full px-3 py-2 border border-[#D2D8D4] rounded-md bg-white"
              data-testid="org-desc-input"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            className="px-4 py-2 rounded-md bg-[#0f1a14] text-white font-semibold disabled:opacity-60 hover:bg-[#1f2a24] transition-colors"
            data-testid="submit-create-org"
          >
            Create
          </button>
        </form>
      )}

      <div className="border border-[#E4E4E7] bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F7F8] border-b border-[#E4E4E7]">
            <tr className="text-left">
              <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-widest">Name</th>
              <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-widest">Status</th>
              <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-widest">Members</th>
              <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-widest">Events</th>
              <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {orgs.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-zinc-500">
                  No organizations yet.
                </td>
              </tr>
            )}
            {orgs.map((o) => (
              <tr key={o.id} className="border-b border-[#E4E4E7] hover:bg-[#F7F7F8]">
                <td className="px-4 py-3">
                  <div className="font-semibold">{o.name}</div>
                  <div className="text-xs text-zinc-500">{o.description}</div>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`px-2 py-1 text-[10px] font-bold uppercase tracking-widest ${
                      o.status === "active"
                        ? "bg-[#00C853] text-white"
                        : "bg-[#FFCC00] text-[#0a0a0a]"
                    }`}
                    data-testid={`org-status-${o.id}`}
                  >
                    {o.status}
                  </span>
                </td>
                <td className="px-4 py-3 mono">{o.member_count}</td>
                <td className="px-4 py-3 mono">{o.event_count}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => toggle(o.id)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#D2D8D4] text-xs font-semibold hover:border-[#0f1a14] hover:bg-[#F4F6F5] transition-colors"
                    data-testid={`toggle-org-${o.id}`}
                  >
                    <Power className="w-3.5 h-3.5" />
                    {o.status === "active" ? "Deactivate" : "Activate"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
